import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter } from 'react-router';
import { RequirePlatform, RequireRole } from './components/Guards';
import { FullPageLoader } from './components/common';

type LazyModule = { default: ComponentType };
const page = (load: () => Promise<LazyModule>) => async () => ({ Component: (await load()).default });

const StaffLayout = lazy(() => import('./layouts/StaffLayout'));
const StudentLayout = lazy(() => import('./layouts/StudentLayout'));
const PlatformLayout = lazy(() => import('./layouts/PlatformLayout'));
const ExamPage = lazy(() => import('./pages/student/Exam'));

function Shell({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageLoader />}>{children}</Suspense>;
}

// Every page is its own chunk: students on slow phones only download what they open.
export const router = createBrowserRouter([
  { path: '/', lazy: page(() => import('./pages/public/Landing')), hydrateFallbackElement: <FullPageLoader /> },
  { path: '/i/:code', lazy: page(() => import('./pages/public/InstituteLogin')), hydrateFallbackElement: <FullPageLoader /> },
  { path: '/platform/login', lazy: page(() => import('./pages/public/PlatformLogin')), hydrateFallbackElement: <FullPageLoader /> },
  {
    path: '/platform',
    element: (
      <RequirePlatform>
        <Shell>
          <PlatformLayout />
        </Shell>
      </RequirePlatform>
    ),
    children: [
      { index: true, lazy: page(() => import('./pages/platform/Institutes')) },
      { path: 'institutes/:id', lazy: page(() => import('./pages/platform/InstituteDetail')) },
      { path: 'demo-requests', lazy: page(() => import('./pages/platform/DemoRequests')) },
      { path: 'pages', lazy: page(() => import('./pages/platform/SitePages')) },
      { path: 'pages/:slug', lazy: page(() => import('./pages/platform/SitePageEditor')) },
      { path: 'account', lazy: page(() => import('./pages/platform/Account')) },
    ],
  },
  {
    path: '/app',
    element: (
      <RequireRole roles={['admin', 'teacher']}>
        <Shell>
          <StaffLayout />
        </Shell>
      </RequireRole>
    ),
    children: [
      { index: true, lazy: page(() => import('./pages/staff/Dashboard')) },
      { path: 'tests', lazy: page(() => import('./pages/staff/Tests')) },
      { path: 'tests/:id', lazy: page(() => import('./pages/staff/TestDetail')) },
      { path: 'questions', lazy: page(() => import('./pages/staff/Questions')) },
      { path: 'questions/new', lazy: page(() => import('./pages/staff/QuestionEditor')) },
      { path: 'questions/:id/edit', lazy: page(() => import('./pages/staff/QuestionEditor')) },
      { path: 'questions/screenshots', lazy: page(() => import('./pages/staff/QuestionScreenshots')) },
      { path: 'questions/import', lazy: page(() => import('./pages/staff/QuestionImport')) },
      { path: 'students', lazy: page(() => import('./pages/staff/Students')) },
      { path: 'students/:id', lazy: page(() => import('./pages/staff/StudentReport')) },
      { path: 'batches', lazy: page(() => import('./pages/staff/Batches')) },
      { path: 'subjects', lazy: page(() => import('./pages/staff/Subjects')) },
      { path: 'staff', lazy: page(() => import('./pages/staff/Staff')) },
      { path: 'settings', lazy: page(() => import('./pages/staff/Settings')) },
      { path: 'account', lazy: page(() => import('./pages/staff/Account')) },
      { path: 'attempts/:attemptId', lazy: page(() => import('./pages/staff/AttemptReview')) },
    ],
  },
  {
    path: '/s/exam/:testId',
    element: (
      <RequireRole roles={['student']}>
        <Shell>
          <ExamPage />
        </Shell>
      </RequireRole>
    ),
  },
  {
    path: '/s',
    element: (
      <RequireRole roles={['student']}>
        <Shell>
          <StudentLayout />
        </Shell>
      </RequireRole>
    ),
    children: [
      { index: true, lazy: page(() => import('./pages/student/Home')) },
      { path: 'results/:attemptId', lazy: page(() => import('./pages/student/Result')) },
      { path: 'leaderboard/:testId', lazy: page(() => import('./pages/student/Leaderboard')) },
      { path: 'progress', lazy: page(() => import('./pages/student/Progress')) },
      { path: 'profile', lazy: page(() => import('./pages/student/Profile')) },
    ],
  },
  { path: '/login', lazy: page(() => import('./pages/public/FindInstitute')), hydrateFallbackElement: <FullPageLoader /> },
  { path: '*', lazy: page(() => import('./pages/public/NotFound')), hydrateFallbackElement: <FullPageLoader /> },
]);
